import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";
import EcoLogoWhite from "../../../img/eco/logo-white.png";
import {
  fmtDateTime,
  fmtScore,
  getQuizPublicInfoApi,
  QuizPublicError,
  startQuizApi,
  submitQuizApi,
  type QuizPublicInfo,
  type QuizStartResult,
  type QuizSubmitResult,
} from "../quizApi";
import QuizReview from "./QuizReview";
import "./QuizPublic.css";

type Answers = Record<string, string[]>;
type Session = { start: QuizStartResult; answers: Answers; index: number };
type Phase = "loading" | "cover" | "answering" | "review" | "result" | "blocked";

const storageKey = (slug: string) => `quiz:${slug}`;

// Sessão da tentativa na aba: sobrevive a recarregar a página. Pode faltar
// (aba anônima, armazenamento bloqueado) e a tela funciona sem ela.
const loadSession = (slug: string): Session | null => {
  try {
    const raw = sessionStorage.getItem(storageKey(slug));
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    return null;
  }
};
const saveSession = (slug: string, session: Session | null) => {
  try {
    if (session) sessionStorage.setItem(storageKey(slug), JSON.stringify(session));
    else sessionStorage.removeItem(storageKey(slug));
  } catch {
    /* sem armazenamento: segue só na memória */
  }
};

const maskCpf = (value: string) => {
  const d = value.replace(/\D/g, "").slice(0, 11);
  return d
    .replace(/^(\d{3})(\d)/, "$1.$2")
    .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1-$2");
};

const QuizPublicPage: React.FC = () => {
  const { slug = "" } = useParams<{ slug: string }>();

  const [phase, setPhase] = useState<Phase>("loading");
  const [info, setInfo] = useState<QuizPublicInfo | null>(null);
  const [blocked, setBlocked] = useState<{ title: string; text: string; tone: "warn" | "ok" | "bad" } | null>(null);
  const [cpf, setCpf] = useState("");
  const [cpfError, setCpfError] = useState("");
  const [busy, setBusy] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  const [result, setResult] = useState<QuizSubmitResult | null>(null);
  const [confirmSend, setConfirmSend] = useState(false);
  const [sendError, setSendError] = useState("");

  const block = (title: string, text: string, tone: "warn" | "ok" | "bad" = "warn") => {
    setBlocked({ title, text, tone });
    setPhase("blocked");
  };

  useEffect(() => {
    (async () => {
      try {
        const data = await getQuizPublicInfoApi(slug);
        setInfo(data);
        const saved = loadSession(slug);
        if (saved && data.status === "ABERTA") {
          setSession(saved);
          setPhase("answering");
        } else {
          setPhase("cover");
        }
      } catch (e: any) {
        block("Quiz não encontrado", e instanceof QuizPublicError && e.status === 404 ? "Confira se o link está completo." : e?.message, "bad");
      }
    })();
  }, [slug]);

  const update = (next: Session) => {
    setSession(next);
    saveSession(slug, next);
  };

  const handleStart = async (e: React.FormEvent) => {
    e.preventDefault();
    setCpfError("");
    if (cpf.replace(/\D/g, "").length !== 11) return setCpfError("Digite os 11 números do CPF.");
    try {
      setBusy(true);
      const start = await startQuizApi(slug, cpf);
      update({ start, answers: {}, index: 0 });
      setPhase("answering");
    } catch (err: any) {
      const status = err instanceof QuizPublicError ? err.status : 0;
      if (status === 409) block("Você já respondeu", "Este quiz já foi enviado com o seu CPF. Obrigado!", "ok");
      else if (status === 410) block("Quiz fechado", err.message);
      else setCpfError(err?.message || "Não foi possível começar.");
    } finally {
      setBusy(false);
    }
  };

  const handleSend = async () => {
    if (!session) return;
    setSendError("");
    try {
      setBusy(true);
      const answers = session.start.questions.map((q) => ({ questionId: q.id, optionIds: session.answers[q.id] ?? [] }));
      const res = await submitQuizApi(slug, session.start.attemptToken, answers);
      saveSession(slug, null);
      setResult(res);
      setConfirmSend(false);
      setPhase("result");
    } catch (err: any) {
      setConfirmSend(false);
      const status = err instanceof QuizPublicError ? err.status : 0;
      if (status === 409) {
        saveSession(slug, null);
        block("Você já respondeu", "Este quiz já foi enviado com o seu CPF.", "ok");
      } else if (status === 410) {
        saveSession(slug, null);
        block("O prazo terminou", "O quiz não aceita mais respostas.");
      } else if (status === 404) {
        // Token trocado (o quiz foi aberto em outro aparelho): começa de novo pelo CPF.
        saveSession(slug, null);
        setSession(null);
        setCpfError("O quiz foi aberto em outro aparelho. Digite o CPF de novo para continuar.");
        setPhase("cover");
      } else {
        setSendError(err?.message || "Não foi possível enviar. Tente de novo.");
      }
    } finally {
      setBusy(false);
    }
  };

  const header = (subtitle?: React.ReactNode) => (
    <header className="quizPub__header">
      <div className="quizPub__headerInner">
        <img className="quizPub__logo" src={EcoLogoWhite} alt="Eco System Serviços" />
        <div className="quizPub__titles">
          <span className="quizPub__kicker">Quiz</span>
          <h1 className="quizPub__title">{session?.start.title ?? info?.title}</h1>
          {subtitle}
        </div>
      </div>
    </header>
  );

  if (phase === "loading") {
    return (
      <div className="quizPub quizPub--center">
        <p className="quizPub__muted">Carregando...</p>
      </div>
    );
  }

  if (phase === "blocked" && blocked) {
    return (
      <div className="quizPub quizPub--status">
        <img className="quizPub__logo" src={EcoLogoWhite} alt="Eco System Serviços" />
        <div className="quizPub__statusBody" role="status">
          <span className={`quizPub__statusIcon quizPub__statusIcon--${blocked.tone}`} aria-hidden="true">
            <EcoIcon name={blocked.tone === "ok" ? "check" : blocked.tone === "bad" ? "x" : "clock"} size={28} />
          </span>
          <h1 className="quizPub__statusTitle">{blocked.title}</h1>
          <p className="quizPub__statusText">{blocked.text}</p>
        </div>
      </div>
    );
  }

  if (phase === "cover" && info) {
    const open = info.status === "ABERTA";
    return (
      <div className="quizPub">
        {header(
          <div className="quizPub__chips">
            <span className="quizPub__chip">
              <EcoIcon name="clock" size={14} /> Válido até {fmtDateTime(info.closesAt)}
            </span>
            <span className="quizPub__chip">{info.questionCount} perguntas</span>
          </div>,
        )}
        <main className="quizPub__main">
          {info.description ? <p className="quizPub__description">{info.description}</p> : null}
          {open ? (
            <form className="quizPub__card" onSubmit={handleStart}>
              <label htmlFor="quiz-cpf" className="quizPub__label">
                Digite o seu CPF para começar
              </label>
              <input
                id="quiz-cpf"
                className="quizPub__input"
                inputMode="numeric"
                autoComplete="off"
                placeholder="000.000.000-00"
                value={cpf}
                onChange={(e) => setCpf(maskCpf(e.target.value))}
                aria-invalid={Boolean(cpfError)}
                aria-describedby={cpfError ? "quiz-cpf-error" : undefined}
              />
              {cpfError ? (
                <span id="quiz-cpf-error" className="quizPub__error">
                  {cpfError}
                </span>
              ) : null}
              <button type="submit" className="quizPub__primary" disabled={busy}>
                {busy ? "Abrindo..." : "Começar"}
              </button>
              <p className="quizPub__muted">Você responde uma vez. As respostas só valem depois de enviar.</p>
            </form>
          ) : (
            <div className="quizPub__card" role="status">
              <strong>{info.status === "AGENDADA" ? "O quiz ainda não abriu." : "O prazo deste quiz terminou."}</strong>
              <span className="quizPub__muted">
                {info.status === "AGENDADA" ? `Abre em ${fmtDateTime(info.opensAt)}.` : "Ele não aceita mais respostas."}
              </span>
            </div>
          )}
        </main>
      </div>
    );
  }

  if ((phase === "answering" || phase === "review") && session) {
    const { questions } = session.start;
    const answeredCount = questions.filter((q) => (session.answers[q.id] ?? []).length).length;

    if (phase === "review") {
      return (
        <div className="quizPub">
          {header(<p className="quizPub__subtitle">Confira antes de enviar, {session.start.name.split(" ")[0]}.</p>)}
          <main className="quizPub__main">
            <ol className="quizPub__reviewList">
              {questions.map((q, i) => {
                const chosen = q.options.filter((o) => (session.answers[q.id] ?? []).includes(o.id));
                return (
                  <li key={q.id} className="quizPub__reviewItem">
                    <span className="quizPub__reviewPrompt">
                      {i + 1}. {q.prompt}
                    </span>
                    <span className={chosen.length ? "quizPub__reviewAnswer" : "quizPub__error"}>
                      {chosen.length ? chosen.map((o) => o.label).join(", ") : "Sem resposta"}
                    </span>
                    <button type="button" className="quizPub__link" onClick={() => { update({ ...session, index: i }); setPhase("answering"); }}>
                      Alterar
                    </button>
                  </li>
                );
              })}
            </ol>
            {sendError ? <p className="quizPub__error">{sendError}</p> : null}
            <div className="quizPub__nav">
              <button type="button" className="quizPub__secondary" onClick={() => setPhase("answering")}>
                Voltar
              </button>
              <button
                type="button"
                className="quizPub__primary"
                disabled={answeredCount < questions.length || busy}
                onClick={() => setConfirmSend(true)}
              >
                Enviar respostas
              </button>
            </div>
            {answeredCount < questions.length ? (
              <p className="quizPub__muted">Responda todas as perguntas para enviar.</p>
            ) : null}
          </main>
          <EcoConfirmDialog
            open={confirmSend}
            title="Enviar respostas?"
            message="Depois de enviar não dá para mudar as respostas."
            confirmLabel={busy ? "Enviando..." : "Enviar"}
            busy={busy}
            onConfirm={handleSend}
            onCancel={() => setConfirmSend(false)}
          />
        </div>
      );
    }

    const index = Math.min(session.index, questions.length - 1);
    const q = questions[index];
    const selected = session.answers[q.id] ?? [];
    const multi = q.type === "MULTI_CHOICE";
    const choose = (optionId: string) => {
      const next = multi
        ? selected.includes(optionId)
          ? selected.filter((x) => x !== optionId)
          : [...selected, optionId]
        : [optionId];
      update({ ...session, answers: { ...session.answers, [q.id]: next } });
    };
    const last = index === questions.length - 1;

    return (
      <div className="quizPub">
        {header(
          <div className="quizPub__progress" aria-label={`Pergunta ${index + 1} de ${questions.length}`}>
            <span className="quizPub__progressTrack">
              <span className="quizPub__progressFill" style={{ width: `${((index + 1) / questions.length) * 100}%` }} />
            </span>
            <span className="quizPub__progressText">
              {index + 1} de {questions.length}
            </span>
          </div>,
        )}
        <main className="quizPub__main">
          <fieldset className="quizPub__question">
            <legend className="quizPub__prompt">{q.prompt}</legend>
            <span className="quizPub__muted">{multi ? "Marque todas as corretas." : "Escolha uma opção."}</span>
            <div className="quizPub__choices">
              {q.options.map((o) => {
                const checked = selected.includes(o.id);
                return (
                  <label key={o.id} className={`quizPub__choice${checked ? " is-selected" : ""}`}>
                    <input
                      type={multi ? "checkbox" : "radio"}
                      name={`q-${q.id}`}
                      checked={checked}
                      onChange={() => choose(o.id)}
                    />
                    <span>{o.label}</span>
                  </label>
                );
              })}
            </div>
          </fieldset>
          <div className="quizPub__nav">
            <button
              type="button"
              className="quizPub__secondary"
              disabled={index === 0}
              onClick={() => update({ ...session, index: index - 1 })}
            >
              Anterior
            </button>
            <button
              type="button"
              className="quizPub__primary"
              disabled={!selected.length}
              onClick={() => (last ? setPhase("review") : update({ ...session, index: index + 1 }))}
            >
              {last ? "Revisar e enviar" : "Próxima"}
            </button>
          </div>
          <p className="quizPub__muted">Válido até {fmtDateTime(session.start.closesAt)}.</p>
        </main>
      </div>
    );
  }

  if (phase === "result" && result) {
    return (
      <div className="quizPub">
        {header()}
        <main className="quizPub__main">
          <div className="quizPub__card quizPub__result" role="status">
            <span className="quizPub__muted">Sua nota</span>
            <strong className="quizPub__score">{fmtScore(result.score)}</strong>
            <span>
              Acertou {result.correctCount} de {result.questionCount} perguntas.
            </span>
            {result.approved !== null ? (
              <span className={`quizPub__verdict${result.approved ? " is-ok" : " is-bad"}`}>
                {result.approved ? "Aprovado" : "Não atingiu a nota mínima"} (mínimo {result.passingScore}%)
              </span>
            ) : null}
            <span className="quizPub__muted">Respostas enviadas. Pode fechar esta página.</span>
          </div>
          {result.answers ? (
            <section className="quizPub__card">
              <h2 className="quizPub__sectionTitle">Gabarito</h2>
              <QuizReview questions={result.answers} />
            </section>
          ) : null}
        </main>
      </div>
    );
  }

  return null;
};

export default QuizPublicPage;
